// FormioForm.jsx
import React, { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { Formio } from "formiojs";
import _ from "lodash";
import "./CustomSelectComponent";
import "./CustomStarRatingComponent"; // Add this import

// GLOBALS
import GLOBALS from "../../globals/index";

// functions
import AJAX from "../../functions/ajax";
import { debounce } from "../../functions/utils";

const FormioForm = ({
  formData,
  votingAvailability,
  options = {},
  hideErrorMsgs = false,
  onChange,
  onSave,
  blockFormSubmission = false,
  intl,
}) => {
  // Stable ID for the form container
  const containerId = useRef(`formio-${Math.random().toString(36).substr(2, 9)}`).current;

  // Refs for form instance and cleanup
  const formInstanceRef = useRef(null);
  // Remove starRender timeouts - no longer needed
  const timeoutRefs = useRef({});

  // Track if we're currently saving to prevent race conditions
  const [isSaving, setIsSaving] = useState(false);

  // Track if form is being edited to prevent unwanted updates
  const [isEditing, setIsEditing] = useState(false);

  // Memoize options to prevent unnecessary re-renders
  const mergedOptions = useMemo(() => {
    let finalOptions = {
      saveDraft: false,
      noAlerts: true,
      baseUrl: "/",
      highlightErrors: false,
      readOnly: blockFormSubmission,
      votingAvailability: votingAvailability,
      intl: intl,
      ...options,
    };

    // Merge i18n options if available
    if (_.isObject(formData?.schema?.i18n)) {
      finalOptions.i18n = finalOptions.i18n || {};
      _.merge(finalOptions.i18n, formData.schema.i18n);
    }

    return finalOptions;
  }, [formData?.schema?.i18n, options, blockFormSubmission, votingAvailability, intl]);

  // Auto-save handler with proper error handling
  const handleAutoSave = useCallback(
    async formInfo => {
      if (!formData.auto_save || !formData.save_api || isSaving) {
        return;
      }

      setIsSaving(true);

      try {
        const response = await AJAX().send(
          containerId,
          formData.save_api.api.method,
          formData.save_api.api.url,
          { data: formInfo.data }
        );

        if (onSave) {
          onSave(response);
        }
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setIsSaving(false);
      }
    },
    [formData.auto_save, formData.save_api, onSave, containerId, isSaving]
  );

  // Create a debounced version with cancel support
  const debouncedAutoSaveRef = useRef(null);

  // Memoize schema JSON to detect actual changes
  const schemaJson = useMemo(() => JSON.stringify(formData.schema), [formData.schema]);
  const availabilityJson = useMemo(() => JSON.stringify(votingAvailability), [votingAvailability]);

  // Debounced auto-save for text inputs
  const debouncedAutoSave = useMemo(() => {
    // Clear previous debounced function if exists
    if (debouncedAutoSaveRef.current) {
      debouncedAutoSaveRef.current = null;
    }

    const debounced = debounce(handleAutoSave, 1000);
    debouncedAutoSaveRef.current = debounced;
    return debounced;
  }, [handleAutoSave]);

  // Update form components dynamically without reinitialization
  const updateFormAvailability = useCallback(() => {
    if (!formInstanceRef.current || !votingAvailability) return;

    const form = formInstanceRef.current;

    // Iterate through all form components and update disabled states
    const updateComponents = components => {
      components.forEach(component => {
        // Check if this is a select component
        if (
          component.component &&
          component.component.type === "select" &&
          component.component.key &&
          votingAvailability[component.component.key]
        ) {
          const availabilityList = votingAvailability[component.component.key];
          const availabilityMap = {};
          const currentValue = component.getValue();

          // Create a map for quick lookup
          availabilityList.forEach(item => {
            availabilityMap[item.value] = item;
          });

          // Update only the disabled state of values
          if (component.component.data && component.component.data.values) {
            component.component.data.values.forEach(valueItem => {
              const avail = availabilityMap[valueItem.value];
              const isCurrentlySelected = String(currentValue) === String(valueItem.value);

              // Only disable if restricted AND not currently selected
              if (avail && !avail.can_vote && !isCurrentlySelected) {
                valueItem.disabled = true;
                valueItem.customProperties = {
                  ...valueItem.customProperties,
                  maxVotesReached: true,
                };
              } else {
                // Remove disabled state if it was previously set
                delete valueItem.disabled;
                if (valueItem.customProperties) {
                  delete valueItem.customProperties.maxVotesReached;
                }
              }
            });
          }

          // Restore the selected value after rebuild
          if (currentValue) {
            component.setValue(currentValue);
          }
        }

        // Recursively process nested components
        if (component.components && component.components.length > 0) {
          updateComponents(component.components);
        }
      });
    };

    // Start updating from root components
    if (form.components) {
      updateComponents(form.components);
    }
  }, [votingAvailability]);

  // Form change handler
  const handleFormChange = useCallback(
    formInfo => {
      // Skip if change events are blocked during render
      if (GLOBALS.blockOnChangeOnFormRender) {
        return;
      }

      if (formInfo.changed) {
        // Notify parent component
        if (onChange) {
          onChange(formInfo);
        }

        // Determine if we should use debounced save
        const changedComponent = formInfo.changed.component;
        const isTextInput =
          changedComponent &&
          (changedComponent.type === "textfield" ||
            changedComponent.type === "textarea" ||
            changedComponent.type === "number" ||
            changedComponent.inputType === "text" ||
            changedComponent.inputType === "number");

        // Use debounced save for text inputs, immediate save for others
        if (isTextInput) {
          debouncedAutoSave(formInfo);
        } else {
          handleAutoSave(formInfo);
        }
      }
    },
    [onChange, handleAutoSave, debouncedAutoSave]
  );

  // Initialize form
  const initializeForm = useCallback(async () => {
    try {
      // Block change events during initialization
      GLOBALS.blockOnChangeOnFormRender = true;

      // Destroy existing form if any
      if (formInstanceRef.current) {
        formInstanceRef.current.destroy();
      }

      // Create new form instance with base schema (no availability applied)
      const form = await Formio.createForm(
        document.getElementById(containerId),
        formData.schema,
        mergedOptions
      );

      // Set initial data - always use the latest content
      form.submission = {
        data: formData.content || {},
      };

      // Store form instance
      formInstanceRef.current = form;

      // Set up event listeners
      form.on("change", handleFormChange);

      form.on("languageChanged", () => {
        form.redraw();
      });

      // No longer need initializeStarRating() - handled by CustomStarRatingComponent

      // Ensure the form renders with the latest data
      if (form.submission && formData.content) {
        form.setValue(formData.content);
      }

      // Apply initial voting availability after form is created
      setTimeout(() => {
        updateFormAvailability();
      }, 100);
    } catch (error) {
      console.error("Failed to initialize form:", error);
    }
  }, [
    schemaJson,
    formData.content,
    mergedOptions,
    handleFormChange,
    containerId,
    updateFormAvailability,
  ]);

  // Enable change events after user interaction
  const handleMouseDown = useCallback(() => {
    GLOBALS.blockOnChangeOnFormRender = false;
    setIsEditing(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    // Delay to ensure form changes are processed
    setTimeout(() => setIsEditing(false), 100);
  }, []);

  // Initialize form only when schema content changes
  useEffect(() => {
    initializeForm();
  }, [schemaJson]); // Re-initialize only when schema changes

  // Update availability without reinitialization
  useEffect(() => {
    updateFormAvailability();
  }, [availabilityJson, updateFormAvailability]);

  // Update form data when content changes (without re-initializing)
  useEffect(() => {
    if (formInstanceRef.current && !isEditing && !isSaving) {
      const currentData = formInstanceRef.current.submission?.data || {};
      const newData = formData.content || {};

      // Only update if data actually changed
      if (JSON.stringify(currentData) !== JSON.stringify(newData)) {
        // Check if any form field is currently focused
        const activeElement = document.activeElement;
        const isFormFieldFocused =
          activeElement && activeElement.closest(`#${containerId}`) !== null;

        if (!isFormFieldFocused) {
          formInstanceRef.current.submission = { data: newData };
        }
      }
    }
  }, [formData.content, containerId, isEditing, isSaving]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up timeouts
      Object.values(timeoutRefs.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });

      // Destroy form instance
      if (formInstanceRef.current) {
        formInstanceRef.current.destroy();
      }

      // Reset global state
      GLOBALS.blockOnChangeOnFormRender = true;

      // Clear debounced function reference
      debouncedAutoSaveRef.current = null;
    };
  }, []);

  // Build CSS classes
  const rootClasses = [
    "formio-root",
    hideErrorMsgs && "hide-error-messages",
    blockFormSubmission && "formioo-form-blocked",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      id="formioo-form"
      className="formioForm"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className={rootClasses}>
        <div id={containerId} />
      </div>
    </div>
  );
};

export default FormioForm;
